// npm imports
import {
  Alert,
  Box,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useQuery } from '@tanstack/react-query'
import ResultsTableV2 from 'Charts/ResultsTableV2'
import 'dayjs/locale/it' // Importa la localizzazione italiana per Dayjs
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'

// local imports
import { listaSelettori } from 'enums/constants-dashboard'
import FilterMenuV2 from 'pages/components/FilterMenuV2'
import SwitchDashboard from 'pages/components/SwitchDashboard'
import { getListaPraticheV2, getListaPraticheV3 } from 'services/getListaPratiche'
import { useFilterListaPraticheStore } from 'stores/filter-store'
import { emptyArrayIfFalsy, zeroIfFalsy } from 'utils/general-utilities'
import OnlyWithFunction, { isValidUser } from '../OnlyWithFunctions'
import Header from 'UI/Header'
import { StyledButton } from 'UI/StyledButton'
import FormFiltriDashboardV2 from 'pages/components/forms-filtri/FormFiltriDashboardV2'
import * as XLSX from 'xlsx'
import dayjs from 'dayjs'

const Dashboard = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState('Errore generico')
  const [openSnackbarAlert, setOpenSnackbarAlert] = useState(false)
  const [openFilterMenu, setOpenFilterMenu] = useState(false)
  const [isExcelRequired, setIsExcelRequired] = useState(false)

  const {
    register,
    setValue,
    getValues,
    formState: { errors },
  } = useForm()


  // Store part
  const { filters, setFilters, resetFilters } = useFilterListaPraticheStore()
  // END Store part

  // Call Apis
  const {
    data: listaPratiche,
    isFetching: isFetchingListaPratiche,
    isFetched: isFetchedListaPratiche,
    refetch: refetchListaPratiche,
  } = useQuery({
    queryFn: async () => {
      try {
        return await getListaPraticheV2(filters)
      } catch (error) {
        setErrorMessage(error.response.data.message)
        setOpenSnackbarAlert(true)
      }
    },
    queryKey: ['listaPratiche', filters],
    enabled: false,
    refetchOnMount: true,
  })

  // END Call Apis

  useEffect(() => {
    if (isExcelRequired) {
      const fetchAndGenerateExcel = async () => {
        try {
          const listaPraticheV3 = await getListaPraticheV3(filters)
          //filters.isGeneratingExcel =false
          generaExcel(listaPraticheV3)
          setIsExcelRequired((prevState) => !prevState)

        } catch (error) {
          // Gestisci eventuali errori
          console.error('Errore durante la chiamata a getListaPraticheV3:', error)
        }
      }

      // Invoca la funzione asincrona senza bloccare il flusso
      fetchAndGenerateExcel()

    }
    refetchListaPratiche()
  }, [filters])


  const fetchPratichePerExcel = () => {

    setIsExcelRequired((prevState) => !prevState)
  }

  const generaExcel = (lista) => {
    if (!lista || lista.length === 0) {
      console.log('Nessun dato disponibile per l\'export.')
      return
    }

    // Mappa i dati per l'export in Excel
    if (isValidUser('UTENTE_INTERNO')) {
      const ws = XLSX.utils.json_to_sheet(
        lista.map(item => ({
          NDG: item.ndg,
          'Codice Fiscale': item.codiceFiscale,
          'Ragione Sociale': item.ragioneSociale,
          'Sale/Field Analyst': item.daAssegnare,
          'Data Apertura': item.dataCreazione ? dayjs(item.dataCreazione).format('DD/MM/YY') : '',
          'Data Chiusura': item.dataChiusura ? dayjs(item.dataChiusura).format('DD/MM/YY') : '',
          'Stato Pratica': item.statoPratica,
          'Tipo Inserimento': item.tipoInserimento,
          'Richiesta Consulenza': item.richiestaSupporto ? 'SI' : 'NO',
          'Esito Monitoraggio': item.esitoMonitoraggio ? 'Si' : 'No',
        })),
      )
      // Crea un nuovo workbook
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Pratiche')

      // Scrive il file Excel con il nome desiderato
      XLSX.writeFile(wb, 'Lista_Pratiche.xlsx')
    } else {
      const ws = XLSX.utils.json_to_sheet(
        lista.map(item => ({
          NDG: item.ndg,
          'Codice Fiscale': item.codiceFiscale,
          'Ragione Sociale': item.ragioneSociale,
          'Sale/Field Analyst': item.daAssegnare,
          'Data Apertura': item.dataCreazione ? dayjs(item.dataCreazione).format('DD/MM/YY') : '',
          'Data Chiusura': item.dataChiusura ? dayjs(item.dataChiusura).format('DD/MM/YY') : '',
          'Stato Pratica': item.statoPratica,
          'Tipo Inserimento': item.tipoInserimento,
          'Richiesta Consulenza': item.richiestaSupporto ? 'SI' : 'NO',
        })),
      )
      // Crea un nuovo workbook
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Pratiche')

      // Scrive il file Excel con il nome desiderato
      XLSX.writeFile(wb, 'Lista_Pratiche.xlsx')
    }


  }


  // Handlers
  const handleChangePage = (page) => {
    setFilters({ ...filters, queryParams: { ...filters.queryParams, pagina: page } })
  }

  const handleChangeSorting = (sorting) => {
    const { field, sort } = sorting
    setFilters({
      ...filters,
      queryParams: {
        ...filters.queryParams,
        sortBy: field,
        sortDirection: sort,
      },
    })
  }

  const handleSelettore = (selettore, event) => {
    setFilters({ ...filters, body: { ...filters.body, selettore: event.target.checked ? selettore : undefined } })
  }

  const onRowClick = (selectedRowData) => {
    navigate(`/dettaglio/${selectedRowData.codiceFiscale}`)
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setFilters({ ...filters, body: { ...filters.body, key: getValues('fieldCerca') } })
  }

  const handleCloseSnackbarAlert = () => {
    setOpenSnackbarAlert(false)
  }

  const handleTastoAnnulla = () => {
    setValue('fieldCerca', '')
    resetFilters()
  }

  // END Handlers

  return <>
    <Header
      showHome={false}
      showRichiesta
      showStatistiche={true}
      showAccounting={true}
      showMonitoraggio={true}
      showSintesiAccounting={true}
      showSintesiAccountingMonitoraggio={true}
      header={
        <Box sx={{ minHeight: '100px' }}>
          <OnlyWithFunction
            acceptedFunction="SEARCH"
            fallbackComponent={
              <Grid
                container
                columns={{ xs: 4, sm: 8, md: 12 }}
                justifyContent="center"
                alignContent="center"
              >
                <Typography
                  sx={{ flexGrow: 1 }}
                  xs={12}
                  item
                  alignItems="center"
                  //mb={'1rem'}
                  color={theme.palette.black.main}
                  variant="h1"
                >
                  ADVR
                </Typography>
              </Grid>
            }
          >
            <Grid
              container
              //sx={{ marginBottom: '1rem' }}
              justifyContent="space-evenly"
              alignContent="space-evenly"
            >
              <form onSubmit={handleSubmit} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '40px',
              }}>
                <Grid item xs={8}>
                  <div>
                    <TextField
                      margin="normal"
                      placeholder="Cerca"
                      required
                      fullWidth
                      variant="standard"
                      helperText=""
                      id="search"
                      name="search"
                      autoComplete="search"
                      autoFocus
                      style={{ flexGrow: 1, marginRight: '10px' }}
                      {...register('fieldCerca')}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <StyledButton
                      sx={{
                        backgroundColor: theme.palette.grey.light,
                        color: theme.palette.black.main,
                        border: '0px',
                        paddingRight: '10px',
                        //mr: 4,
                        //mt: 1,
                      }}
                      type="submit"
                    >
                      <Typography variant="caption">CERCA</Typography>
                    </StyledButton>
                    <StyledButton
                      onClick={handleTastoAnnulla}
                      sx={{
                        backgroundColor: theme.palette.grey.light,
                        color: theme.palette.black.main,
                        border: '0px',
                        paddingRight: '10px',
                        //mr: 4,
                        //mt: 1,
                      }}
                    >
                      <Typography variant="caption">ANNULLA</Typography>
                    </StyledButton>
                  </div>

                </Grid>
                <Grid item xs={4} marginBottom={-1} justifySelf="end" alignSelf="start" marginTop={1}>

                  {listaSelettori.map(sel => {
                    return sel.ruoli.filter(role => (role === 'TUTTI' || isValidUser(role))).length > 0 ?
                      <SwitchDashboard key={sel.descrizione + sel.codiceSelettore} description={sel.descrizione}
                                       isChecked={filters.body.selettore === sel.codiceSelettore}
                                       onChangeSwitch={(event) => handleSelettore(sel.codiceSelettore, event)}
                                       isDisabled={isFetchingListaPratiche} /> : <></>
                  })}

                </Grid>

              </form>
            </Grid>
          </OnlyWithFunction>
        </Box>
      }
    />


    {/* Table pratiche */}
    <Box sx={{ marginRight: '2vw', marginLeft: '2vw', marginTop: '20px' }}>
      <FilterMenuV2 {...{ openFilterMenu, setOpenFilterMenu }}>
        <FormFiltriDashboardV2 {...{ setOpenFilterMenu, fetchPratichePerExcel }} />
      </FilterMenuV2>


      <ResultsTableV2 listaPratiche={emptyArrayIfFalsy(isFetchedListaPratiche, listaPratiche?.content)}
                      totalElements={zeroIfFalsy(isFetchedListaPratiche, listaPratiche?.totalElements)}
                      pageSize={filters.queryParams.elementi} page={filters.queryParams.pagina} {...{
        isFetchingListaPratiche,
        handleChangePage,
        handleChangeSorting,
        onRowClick,
      }} />
    </Box>

    {/* Alert per quando chiamata va in errore */}
    <Snackbar open={openSnackbarAlert} autoHideDuration={5000} onClose={handleCloseSnackbarAlert}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert
        onClose={handleCloseSnackbarAlert}
        severity="error"
        variant="filled"
        sx={{ width: '100%' }}
      >
        {errorMessage}
      </Alert>
    </Snackbar>
  </>
}

export default Dashboard